// components/events/EventForm.jsx
import React, { useState, useEffect } from "react";
import { Form, Button, InputGroup, Spinner, Image } from "react-bootstrap";
import { useDispatch } from "react-redux";
import {
  createEventAction,
  updateEventAction,
} from "../../pages/events/EventAction";
import { CustomInput } from "../custom-input/CustomInput";

const initialState = {
  name: "",
  description: "",
  startDate: "",
  endDate: "",
  location: "",
  organizers: [""],
  performers: [""],
  mainImage: "",
  gallery: [],
  url: "",
};

export const EventForm = ({ eventId, initialData, selectedEvent, onClose }) => {
  const dispatch = useDispatch();
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);

  // Seed from initial data (list or fetched)
  useEffect(() => {
    if (eventId && initialData?._id === eventId) {
      setForm({ ...initialState, ...initialData });
    } else if (!eventId) {
      setForm(initialState);
    }
  }, [eventId, initialData]);

  useEffect(() => {
    if (eventId && selectedEvent?._id === eventId) {
      setForm((prev) => ({ ...prev, ...selectedEvent }));
    }
  }, [eventId, selectedEvent]);

  const isEventEnded =
    form.endDate && new Date(form.endDate).getTime() < Date.now();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleOrganizersChange = (e) => {
    const values = e.target.value
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
    setForm((prev) => ({ ...prev, organizers: values }));
  };

  const handlePerformersChange = (e) => {
    const values = e.target.value
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
    setForm((prev) => ({ ...prev, performers: values }));
  };

  // Gallery handlers
  const handleGalleryChange = (index, value) => {
    const updated = [...form.gallery];
    updated[index] = value;
    setForm((prev) => ({ ...prev, gallery: updated }));
  };

  const handleAddGalleryField = () => {
    setForm((prev) => ({ ...prev, gallery: [...prev.gallery, ""] }));
  };

  const handleRemoveGalleryField = (index) => {
    const updated = form.gallery.filter((_, i) => i !== index);
    setForm((prev) => ({ ...prev, gallery: updated }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (eventId) {
        await dispatch(updateEventAction(eventId, form));
      } else {
        await dispatch(createEventAction(form));
      }
      onClose?.();
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit}>
      {submitting && <Spinner animation="border" size="sm" className="mb-3" />}

      <CustomInput
        name="name"
        label="Event Name*"
        value={form.name}
        onChange={handleChange}
        required
      />

      <CustomInput
        name="description"
        label={
          isEventEnded
            ? "Description (Add more details if needed)"
            : "Description*"
        }
        as="textarea"
        rows={isEventEnded ? 6 : 3}
        value={form.description}
        onChange={handleChange}
        required
      />

      <CustomInput
        name="startDate"
        label="Start Date*"
        type="date"
        value={
          form.startDate
            ? new Date(form.startDate).toISOString().split("T")[0]
            : ""
        }
        onChange={handleChange}
        required
      />

      <CustomInput
        name="endDate"
        label="End Date*"
        type="date"
        value={
          form.endDate ? new Date(form.endDate).toISOString().split("T")[0] : ""
        }
        onChange={handleChange}
        required
      />

      <CustomInput
        name="location"
        label="Location*"
        value={form.location}
        onChange={handleChange}
        required
      />

      <CustomInput
        name="organizers"
        label="Organizers (comma separated)*"
        value={form.organizers.join(", ")}
        onChange={handleOrganizersChange}
        required
      />

      <CustomInput
        name="performers"
        label="Performers (comma separated)"
        value={form.performers.join(", ")}
        onChange={handlePerformersChange}
      />

      <CustomInput
        name="mainImage"
        label="Main Image URL*"
        value={form.mainImage}
        onChange={handleChange}
        required
      />
      {form.mainImage && (
        <Image
          src={form.mainImage}
          thumbnail
          className="my-2"
          width={100}
          height={100}
        />
      )}

      {/* Gallery only if event ended */}
      {isEventEnded && (
        <Form.Group className="mb-3">
          <Form.Label>Gallery Images</Form.Label>
          {form.gallery.map((url, idx) => (
            <InputGroup className="mb-2" key={idx}>
              <Form.Control
                type="text"
                placeholder={`Image URL #${idx + 1}`}
                value={url}
                onChange={(e) => handleGalleryChange(idx, e.target.value)}
              />
              <Button
                variant="outline-danger"
                onClick={() => handleRemoveGalleryField(idx)}
              >
                🗑
              </Button>
            </InputGroup>
          ))}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleAddGalleryField}
          >
            ➕ Add Another Image
          </Button>

          {/* Live previews */}
          <div className="mt-2 d-flex flex-wrap gap-2">
            {form.gallery
              .filter((url) => url)
              .map((url, idx) => (
                <Image key={idx} src={url} thumbnail width={100} height={100} />
              ))}
          </div>
        </Form.Group>
      )}

      <CustomInput
        name="url"
        label="External URL"
        value={form.url}
        onChange={handleChange}
        placeholder="https://example.com"
      />

      <div className="d-grid py-3">
        <Button type="submit" disabled={submitting}>
          {eventId ? "Update Event" : "Submit Event"}
        </Button>
      </div>
    </Form>
  );
};
